import { VFABebiePoint } from "types/tests/specifics";

const SWAP_TOP_BEHAVIOUR = "6";
const SWAP_EQUIVALENT_DB_OFFSET = 12;

interface ReliabilityCounter {
  val?: number;
  total?: number;
}

interface ReliabilityInfo {
  fp?: ReliabilityCounter;
  fn?: ReliabilityCounter;
}

function withoutIndexes(values: number[], ignoredIndexes: number[]): number[] {
  const ignored = new Set(ignoredIndexes);
  return values.filter((_value, index) => !ignored.has(index));
}

export function getDisplayedThresholds(
  points: number[],
  testBehaviour?: string | number,
): number[] {
  if (String(testBehaviour) !== SWAP_TOP_BEHAVIOUR) return [...points];

  return points.map((point) => (point === 0 ? 0 : point + SWAP_EQUIVALENT_DB_OFFSET));
}

export function calculateMeanSensitivity(
  displayedThresholds: number[],
  ignoredIndexes: number[],
): number {
  const included = withoutIndexes(displayedThresholds, ignoredIndexes);
  if (included.length === 0) return 0;
  return included.reduce((sum, value) => sum + value, 0) / included.length;
}

export function calculateReliabilityFactor(reliability?: ReliabilityInfo): number | null {
  const counters = [reliability?.fp, reliability?.fn].filter(
    (counter): counter is ReliabilityCounter => counter != null,
  );
  const totalChecks = counters.reduce(
    (sum, counter) => sum + Math.max(0, Number(counter.total) || 0),
    0,
  );
  if (totalChecks === 0) return null;

  const incorrectAnswers = counters.reduce(
    (sum, counter) => sum + Math.max(0, Number(counter.val) || 0),
    0,
  );
  return (incorrectAnswers / totalChecks) * 100;
}

export function calculateBebieCurveFromTotalDeviation(
  totalDeviation: number[],
  ignoredIndexes: number[],
): VFABebiePoint[] {
  const ignored = new Set(ignoredIndexes);
  return totalDeviation
    .filter((value, index) => !ignored.has(index) && Number.isFinite(value))
    .map((value) => -value)
    .sort((a, b) => a - b)
    .map((defect, index) => ({ rank: index + 1, defect }));
}
